import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ContextService } from './context.service';
import { concatMap, from, map, Observable, of, reduce, switchMap } from 'rxjs';
import {
  Entity,
  EntityTypeChange,
  Octopus,
  WorkflowStageTemplateProperty,
} from '@maximizer/core/shared/domain';
import { WorkflowTaskTemplateService } from './workflow-task-template.service';

@Injectable()
export class WorkflowStageTemplateService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
    private readonly workflowTaskTemplateService: WorkflowTaskTemplateService,
  ) {}

  read(key?: string): Observable<WorkflowStageTemplateProperty[]> {
    const searchQuery: Octopus.SearchQuery<Octopus.WorkflowStageTemplate> = key
      ? {
          WorkflowKey: {
            $EQ: key,
          },
        }
      : {};
    const request: Octopus.WorkflowStageTemplateReadRequest = {
      WorkflowStage: {
        Scope: {
          Fields: {
            Key: 1,
            Name: 1,
            Sequence: 1,
            WorkflowKey: 1,
            IsSequential: 1,
          },
        },
        Criteria: {
          SearchQuery: searchQuery,
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowStageTemplateReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.WorkflowStageTemplateMapper();
            return mapper.from(result.WorkflowStage.Data);
          }
          return [];
        }),
      );
  }

  update(values: WorkflowStageTemplateProperty): Observable<string | null> {
    const request = {
      WorkflowStage: {
        Data: {
          Key: values.key ?? null,
          Name: values.name,
          Sequence: values.sequence,
          WorkflowKey: values.workflowKey,
          IsSequential: values.isSequential,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowStageTemplateWriteResponse>(
        `${this.context.api}${values.key ? Octopus.Action.UPDATE : Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.WorkflowStage.Data.Key;
          }
          return null;
        }),
      );
  }

  copy(
    values: WorkflowStageTemplateProperty[],
    entityTypeChange?: EntityTypeChange,
  ): Observable<boolean> {
    if (values.length === 0) return of(true);

    return from(values).pipe(
      concatMap((stage) =>
        this.update({ ...stage, key: null }).pipe(
          switchMap((newStageKey) => {
            if (!newStageKey) return of(false);
            if (!stage.key) return of(true);
            return this.workflowTaskTemplateService
              .read({ workflowKey: stage.key })
              .pipe(
                switchMap((tasks) => {
                  let list: Entity[] = [];
                  if (Array.isArray(tasks)) {
                    list = tasks;
                  } else if (tasks != null) {
                    list = [tasks];
                  }
                  return list.length
                    ? this.workflowTaskTemplateService.copy(
                        newStageKey,
                        list,
                        entityTypeChange,
                      )
                    : of(true);
                }),
              );
          }),
        ),
      ),
      reduce((acc, ok) => acc && ok, true),
    );
  }

  delete(key: string): Observable<boolean> {
    const request = {
      WorkflowStage: {
        Data: {
          Key: key,
        },
      },
    };
    return this.http
      .post<Octopus.WorkflowStageTemplateWriteResponse>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return true;
          }
          return false;
        }),
      );
  }

  checkDuplicated(workflowKey: string, name: string): Observable<boolean> {
    const request: Octopus.WorkflowStageTemplateReadRequest = {
      WorkflowStage: {
        Scope: {
          Fields: {
            Key: 1,
          },
        },
        Criteria: {
          SearchQuery: {
            $AND: [
              {
                WorkflowKey: {
                  $EQ: workflowKey,
                },
              },
              {
                Name: {
                  $EQ: name,
                },
              },
            ],
          },
        },
      },
    };

    return this.http
      .post<Octopus.WorkflowStageTemplateReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            return result.WorkflowStage?.Data?.length > 0;
          }
          return false;
        }),
      );
  }

  hasTasksInEachStage(workflowKey: string): Observable<boolean> {
    return this.read(workflowKey).pipe(
      switchMap((stages) => {
        const stageKeys = stages
          .map((s) => s.key ?? '')
          .filter((k): k is string => Boolean(k));
        if (stageKeys.length === 0) return of(false);
        return this.workflowTaskTemplateService
          .read({ workflowKeys: stageKeys })
          .pipe(
            map((result) =>
              this.hasTasksInStage(
                stageKeys,
                this.taskTemplatesForStage(result),
              ),
            ),
          );
      }),
    );
  }

  private taskTemplatesForStage(result: Entity | Entity[] | null): Entity[] {
    if (Array.isArray(result)) return result;
    if (result != null) return [result];
    return [];
  }

  private hasTasksInStage(stageKeys: string[], tasks: Entity[]): boolean {
    const workflowKeyField = Octopus.WorkflowTaskTemplateFields.WorkflowKey;
    return stageKeys.every((stageKey) =>
      tasks.some(
        (task) =>
          (task as Record<string, string>)[workflowKeyField] === stageKey,
      ),
    );
  }
}
